import type { ComposerTranslation } from 'vue-i18n';
import {
    ActionType,
    type Button,
    type Checkbox,
    type ContainerElement,
    type DateInput,
    type Divider,
    ElementType,
    type Form,
    type FormElement,
    type Header,
    HeaderSize,
    type NumberInput,
    type SelectInput,
    type SelectOption,
    type Text,
    type TextInput,
    TextSize,
    type VisibilityRule,
    VisibilityRuleType
} from './types';
import { mapToId } from 'vince-core/utils/collections';
import { Comparison, ResourceType } from 'vince-core/types/common';
import { shortId } from 'vince-ui/utils/string';
import { ButtonType } from 'vince-ui/types/common';

export interface SupportedSourceConfig {
    id: ResourceType;
    component: Component;
}

export const SUPPORTED_SOURCES = [
    {
        id: ResourceType.META,
        component: defineAsyncComponent(() => import('./components/TableSource.vue'))
    }
] as SupportedSourceConfig[];


export interface FormElementConfig {
    id: ElementType;
    elementLabel: string;
    elementDescription?: string;
    previewComponent: Component; // Used in list of available components
    builderComponent: Component; // Used to represent element in form builder
    propertiesEditorComponent?: Component; // Used to edit elements properties
    formComponent: Component; // Used as actual component that user interacts with
    create: (parent: ContainerElement, t: ComposerTranslation) => FormElement;
    canBeAdded: (parent: ContainerElement) => boolean;
}


export const FORM_ELEMENTS_CONFIG = [
    {
        id: ElementType.HEADER,
        elementLabel: 'formBuilder.elements.HEADER.label',
        elementDescription: 'formBuilder.elements.HEADER.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/header/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/header/Header.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.HEADER,
                size: HeaderSize.H2,
                label: t('formBuilder.elements.HEADER.defaultText')
            } as Header;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.TEXT,
        elementLabel: 'formBuilder.elements.TEXT.label',
        elementDescription: 'formBuilder.elements.TEXT.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/text/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/text/Text.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.TEXT,
                size: TextSize.MAIN,
                label: t('formBuilder.elements.TEXT.defaultText')
            } as Text;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.TEXT_INPUT,
        elementLabel: 'formBuilder.elements.TEXT_INPUT.label',
        elementDescription: 'formBuilder.elements.TEXT_INPUT.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/text-input/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/text-input/TextInput.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.TEXT_INPUT,
                label: t('formBuilder.elements.TEXT_INPUT.defaultText')
            } as TextInput;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.NUMBER_INPUT,
        elementLabel: 'formBuilder.elements.NUMBER_INPUT.label',
        elementDescription: 'formBuilder.elements.NUMBER_INPUT.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/number-input/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/number-input/NumberInput.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.NUMBER_INPUT,
                label: t('formBuilder.elements.NUMBER_INPUT.defaultText'),
                minValue: null,
                maxValue: null,
                showFractionDigits: false,
                prefix: undefined
            } as NumberInput;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.DATE_INPUT,
        elementLabel: 'formBuilder.elements.DATE_INPUT.label',
        elementDescription: 'formBuilder.elements.DATE_INPUT.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/date-input/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/date-input/DateInput.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.DATE_INPUT,
                label: t('formBuilder.elements.DATE_INPUT.defaultLabel'),
                withTime: false
            } as DateInput;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.SELECT_INPUT,
        elementLabel: 'formBuilder.elements.SELECT_INPUT.label',
        elementDescription: 'formBuilder.elements.SELECT_INPUT.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/select-input/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/select-input/SelectInput.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.SELECT_INPUT,
                label: t('formBuilder.elements.SELECT_INPUT.defaultLabel'),
                options: [] as SelectOption[]
            } as SelectInput;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.CHECKBOX,
        elementLabel: 'formBuilder.elements.CHECKBOX.label',
        elementDescription: 'formBuilder.elements.CHECKBOX.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/checkbox/Editor.vue')),
        formComponent: defineAsyncComponent(() => import('./components/checkbox/Checkbox.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.CHECKBOX,
                label: t('formBuilder.elements.CHECKBOX.defaultLabel'),
                trueValue: undefined,
                falseValue: undefined
            } as Checkbox;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.DIVIDER,
        elementLabel: 'formBuilder.elements.DIVIDER.label',
        elementDescription: 'formBuilder.elements.DIVIDER.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        formComponent: defineAsyncComponent(() => import('./components/divider/Divider.vue')),
        create: (_parent: ContainerElement, _t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.DIVIDER
            } as Divider;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.BUTTON,
        elementLabel: 'formBuilder.elements.BUTTON.label',
        elementDescription: 'formBuilder.elements.BUTTON.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/Element.vue')),
        formComponent: defineAsyncComponent(() => import('./components/button/Button.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/button/Editor.vue')),
        create: (_parent: ContainerElement, t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.BUTTON,
                label: t('formBuilder.elements.BUTTON.defaultLabel'),
                buttonType: ButtonType.PRIMARY,
                action: { type: ActionType.CANCEL }
            } as Button;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    },
    {
        id: ElementType.CONTAINER,
        elementLabel: 'formBuilder.elements.CONTAINER.label',
        elementDescription: 'formBuilder.elements.CONTAINER.description',
        previewComponent: defineAsyncComponent(() => import('./components/NewElement.vue')),
        builderComponent: defineAsyncComponent(() => import('./components/container/ContainerBuilder.vue')),
        formComponent: defineAsyncComponent(() => import('./components/container/Container.vue')),
        propertiesEditorComponent: defineAsyncComponent(() => import('./components/container/Editor.vue')),
        create: (_parent: ContainerElement, _t: ComposerTranslation): FormElement => {
            return {
                id: shortId(),
                type: ElementType.CONTAINER,
                direction: 'horizontal',
                children: [] as FormElement[],
                visibilityRules: [] as VisibilityRule[]
            } as ContainerElement;
        },
        canBeAdded: (_parent: ContainerElement) => {
            return true;
        }
    }
] as FormElementConfig[];

export const FormElementsConfigMap = mapToId(FORM_ELEMENTS_CONFIG, 'id') as Record<ElementType, FormElementConfig>;


export const emptyForm = (t: ComposerTranslation): Form => {
    return <Form>{
        id: shortId(),
        label: t('formBuilder.emptyFormLabel'),
        description: t('formBuilder.emptyFormDescription'),
        container: {
            id: shortId(),
            position: 0,
            direction: 'vertical',
            type: ElementType.CONTAINER,
            children: [
                // FormElementsConfigMap.CONTAINER.create({} as unknown as ContainerElement, t)
            ] as FormElement[],
            visibilityRules: [] as VisibilityRule[]
        }
    };
};

export const createVisibilityRule = (): VisibilityRule => {
    return <VisibilityRule>{
        type: VisibilityRuleType.CONTEXT,
        field: 'tags',
        comparison: Comparison.contains,
        value: ''
    };
};
